/**
 * Minimal HTML sanitiser for rich-text comment bodies.
 *
 * Why a hand-rolled walker? `isomorphic-dompurify` ships its own
 * JSDOM that doesn't cooperate with happy-dom (vitest's environment)
 * and returns an empty string when input contains a `<script>` —
 * which means the test environment doesn't agree with the browser.
 * That's a non-starter for the security-critical layer.
 *
 * The walker below uses `Range.createContextualFragment()` to parse
 * inert HTML — per the HTML spec, scripts inside a fragment that's
 * not yet connected to the document never execute. Works identically
 * in happy-dom, jsdom, and every real browser.
 *
 * Threat model:
 *   * The `<RichTextEditor>` (TipTap with B/I/U/list/link) is the
 *     intended producer; we trust its output.
 *   * Anyone can POST to `/api/v1/tickets/:id/comments` directly and
 *     bypass the editor. That payload could contain `<script>`,
 *     `onclick=`, `<iframe src="javascript:…">`, etc.
 *   * Sanitisation runs on render so a malicious comment body lands
 *     as inert text in another user's browser, never as executable.
 *
 * Allow-listed tags + attrs match the editor's mark/node set. Anything
 * else is removed entirely.
 */

const ALLOWED_TAGS = new Set([
  "p", "br", "strong", "b", "em", "i", "u", "s",
  "ul", "ol", "li", "a", "code", "blockquote",
]);

const ALLOWED_ATTRS_BY_TAG: Record<string, Set<string>> = {
  a: new Set(["href", "target", "rel", "class"]),
};

const SAFE_URL_PREFIX = /^(https?:|mailto:|\/|#|\.\.?\/|[\w.-]+\/)/i;

export function sanitizeRichText(html: string): string {
  if (
    typeof document === "undefined" ||
    typeof document.createRange !== "function"
  ) {
    // Fallback for unusual environments — return a tag-stripped string.
    return html.replace(/<[^>]*>/g, "");
  }

  // Parse into an *inert* DocumentFragment via Range. The fragment is
  // never attached to the live document, so per the HTML spec scripts
  // inside it don't execute (this is the same model `<template>` uses,
  // but Range works in older environments too).
  const range = document.createRange();
  // The range needs a context node for HTML parsing; document.body is
  // fine because we never attach the resulting fragment.
  range.selectNode(document.body);
  const fragment = range.createContextualFragment(html);

  walk(fragment);

  // Re-serialise. Wrap in a DIV so we can read `innerHTML` from a
  // single element — DocumentFragment doesn't have an innerHTML
  // getter on its own.
  const wrap = document.createElement("div");
  wrap.appendChild(fragment);
  return wrap.innerHTML;
}

function walk(node: ParentNode): void {
  const children = Array.from(node.children);
  for (const child of children) {
    const tag = child.tagName.toLowerCase();

    if (!ALLOWED_TAGS.has(tag)) {
      child.remove();
      continue;
    }

    const allowedAttrs = ALLOWED_ATTRS_BY_TAG[tag] ?? EMPTY_SET;
    for (const attr of Array.from(child.attributes)) {
      if (!allowedAttrs.has(attr.name.toLowerCase())) {
        child.removeAttribute(attr.name);
      }
    }

    if (tag === "a") {
      const href = child.getAttribute("href") ?? "";
      if (!SAFE_URL_PREFIX.test(href)) {
        child.removeAttribute("href");
      }
      child.setAttribute("target", "_blank");
      child.setAttribute("rel", "noopener noreferrer");
    }

    walk(child);
  }
}

const EMPTY_SET: Set<string> = new Set();
